import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@arcadeai/design-system/components/ui/atoms/dialog";
import { Input } from "@arcadeai/design-system/components/ui/atoms/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@arcadeai/design-system/components/ui/atoms/select";
import { cn } from "@arcadeai/design-system/lib/utils";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@arcadeai/design-system/rhf";
import { zodResolver } from "@hookform/resolvers/zod";
import { Building2, Loader2, UserPlus } from "lucide-react";
import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useCreateProjectInvitation } from "@/app/invitations/api/hooks";
import {
  type CreateInvitationFormValues,
  createInvitationSchema,
} from "@/app/invitations/types";
import type { Project } from "@/app/projects/types";
import { useAuthzEnabled, useProjectRoles } from "@/app/roles/api/hooks";
import { getRoleStyle } from "@/app/roles/lib/role-style";
import { ROLE } from "@/app/roles/types";
import { InvitePermissionWarning } from "./invite-permission-warning";

type InviteMemberContentProps = {
  onClose: () => void;
  project?: Project;
};

const InviteMemberContent = ({
  onClose,
  project,
}: InviteMemberContentProps) => {
  const emailInputRef = useRef<HTMLInputElement | null>(null);
  const { mutate: inviteMember, isPending } = useCreateProjectInvitation();
  const authzEnabled = useAuthzEnabled();
  const { roles } = useProjectRoles({ invitableOnly: true });

  const form = useForm<CreateInvitationFormValues>({
    resolver: zodResolver(createInvitationSchema),
    defaultValues: {
      email: "",
      // Least-privilege default. Creating an invite requires invitations_create,
      // which only Project Admin / Org Admin hold, and both outrank Project
      // Member — so this default is always within the inviter's grant ceiling.
      // The picker lets them upgrade to Project Admin.
      role: ROLE.projectMember,
    },
  });

  const onSubmit = async (data: CreateInvitationFormValues) => {
    await inviteMember(
      {
        email: data.email,
        ...(authzEnabled && data.role ? { role: data.role } : {}),
      },
      {
        onSuccess: () => {
          form.reset();
          onClose();
        },
      }
    );
  };

  return (
    <DialogContent className="sm:max-w-lg" initialFocus={emailInputRef}>
      <DialogHeader>
        <DialogTitle>Invite Member</DialogTitle>
        <DialogDescription>
          Add a team member by email and we'll send them an invite to
          collaborate on this project.
        </DialogDescription>
      </DialogHeader>

      {!!project && (
        <div className="flex items-center gap-3 rounded-lg border bg-muted/50 p-3">
          <div
            className={cn(
              "flex shrink-0 items-center justify-center rounded bg-blue-700 shadow-sm",
              project.description ? "size-8" : "size-6"
            )}
          >
            <Building2 className="size-4 text-white" />
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-0.5 overflow-hidden">
            <span className="truncate font-medium text-sm">{project.name}</span>
            {!!project.description && (
              <span className="truncate text-muted-foreground text-xs">
                {project.description}
              </span>
            )}
          </div>
        </div>
      )}

      {!authzEnabled && <InvitePermissionWarning />}

      <Form {...form}>
        <form
          className="flex flex-col gap-4"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email Address</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    disabled={isPending}
                    placeholder="Enter email address"
                    ref={(element) => {
                      field.ref(element);
                      emailInputRef.current = element;
                    }}
                    type="email"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {authzEnabled && roles.length > 0 && (
            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Role</FormLabel>
                  <Select
                    disabled={isPending}
                    onValueChange={(value) => value && field.onChange(value)}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue>
                          {(value) =>
                            roles.find((role) => role.key === value)
                              ?.display_name ?? "Select a role"
                          }
                        </SelectValue>
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {roles.map((role) => {
                        const { icon: Icon } = getRoleStyle(role.key);
                        return (
                          <SelectItem key={role.key} value={role.key}>
                            <span className="flex items-center gap-2">
                              <Icon className="size-3.5 text-muted-foreground" />
                              {role.display_name}
                            </span>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Controls what the invitee can do once they join.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <DialogFooter>
            <DialogClose
              render={
                <Button
                  disabled={isPending}
                  onClick={() => form.reset()}
                  size="sm"
                  type="button"
                  variant="outline"
                />
              }
            >
              Cancel
            </DialogClose>
            <Button disabled={isPending} size="sm" type="submit">
              {isPending && <Loader2 className="size-4 animate-spin" />}
              Send Invitation
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </DialogContent>
  );
};

type InviteMemberModalProps = {
  trigger?: React.ReactElement;
  project?: Project;
};

export const InviteMemberModal = ({
  trigger,
  project,
}: InviteMemberModalProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Dialog onOpenChange={setIsOpen} open={isOpen}>
      {trigger ? (
        <DialogTrigger render={trigger} />
      ) : (
        <DialogTrigger render={<Button size="sm" />}>
          <UserPlus className="mr-2 size-4" />
          Invite Member
        </DialogTrigger>
      )}
      <InviteMemberContent onClose={() => setIsOpen(false)} project={project} />
    </Dialog>
  );
};
